import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

const AD_CLIENT = 'ca-pub-5364211267212570';
const ADSENSE_SCRIPT_ID = 'google-adsense-script';

/**
 * Routes where Google ads are allowed (publisher content pages only).
 * Do NOT include account, checkout, upload, empty utility, or success/error screens.
 */
const ADS_ALLOWED_PATHS: Array<string | RegExp> = [
  /^\/$/,
  /^\/dashboard\/?$/,
  /^\/explore-items(\/[^/]+)?\/?$/,
  /^\/productdetail\/[^/]+\/[^/]+\/?$/,
  /^\/public\/[^/]+\/profile\/?$/,
  /^\/blog_list\/?$/,
  /^\/blog_details\/?$/,
  /^\/about-us\/?$/,
  /^\/contact-us\/?$/,
  /^\/faq\/?$/,
  /^\/privacy\/?$/,
  /^\/terms-of-use\/?$/,
  /^\/license\/?$/,
  /^\/plan\/?$/,
  /^\/creator\/?$/,
  /^\/benefits\/?$/,
  /^\/offer\/?$/,
];

export function isAdsAllowedPath(pathname: string): boolean {
  return ADS_ALLOWED_PATHS.some((rule) =>
    typeof rule === 'string' ? rule === pathname : rule.test(pathname)
  );
}

function removeInjectedAds(): void {
  document
    .querySelectorAll(
      'ins.adsbygoogle, iframe[src*="googleads"], iframe[src*="googlesyndication"], div[id^="google_ads_iframe"]'
    )
    .forEach((el) => el.remove());
}

/**
 * Loads AdSense only on content-rich routes so Auto ads cannot appear
 * on account / empty / transactional screens (AdSense policy).
 */
export function useAdSenseOnContentPages(): void {
  const { pathname } = useLocation();

  useEffect(() => {
    const allowed = isAdsAllowedPath(pathname);
    const existing = document.getElementById(ADSENSE_SCRIPT_ID);

    if (!allowed) {
      if (existing) {
        existing.remove();
      }
      removeInjectedAds();
      return;
    }

    if (existing) return;

    const script = document.createElement('script');
    script.id = ADSENSE_SCRIPT_ID;
    script.async = true;
    script.crossOrigin = 'anonymous';
    script.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${AD_CLIENT}`;
    document.head.appendChild(script);
  }, [pathname]);
}
